import { Chip, Grid2 as Grid, IconButton, Stack, StackProps, Typography } from '@mui/material';
import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import { Eye, Trash01 } from 'untitledui-js-base';
import { parseDate } from '../../utils/parseDate';
import { TransparentChip } from '../common';
import { RentalInfoBox } from '../rental/RentalInfoBox';
import { Accident } from '../types/accident';

export interface AccidentTagsProps extends StackProps {
  accident: Pick<Accident, 'status' | 'labels'>;
}

export const AccidentTags = ({ accident: { status, labels }, ...props }: AccidentTagsProps) => (
  <Stack direction="column" spacing={0.5} alignItems="flex-start" {...props}>
    {status && (
      <Chip
        size="small"
        label={status.label}
        variant="filled"
        sx={{
          backgroundColor: status.color
        }}
      />
    )}
    {labels?.map((label) => (
      <TransparentChip key={label.type} size="small" color="info" label={label.label} />
    ))}
  </Stack>
);

interface AccidentMerchantItemProps {
  accident: Accident;
  onClick?: () => void;
  onRentalClick?: (id: number) => void;
  onRemove?: () => void;
}

export const AccidentMerchantItem = ({ accident, onClick, onRentalClick, onRemove }: AccidentMerchantItemProps) => (
  <Grid container spacing={2} alignItems="center" width={1} position="relative">
    <Grid size={{ xs: 5, lg: 1 }}>
      <Typography color="gray">
        {accident.date ? format(parseDate(accident.date) ?? '', 'dd.MM.yyyy') : 'N/A'}
      </Typography>
    </Grid>
    <Grid size={{ xs: 7, lg: 2 }}>
      <AccidentTags accident={accident} />
    </Grid>
    <Grid size={{ xs: 12, lg: 3 }}>
      <Stack direction="row" spacing={1}>
        <Stack>
          <Typography fontWeight="bold" onClick={onClick} sx={{ cursor: 'pointer' }}>
            {accident.user
              ? [
                  accident.user.company?.companyName,
                  [accident.user.surname, accident.user.name].filter(Boolean).join(' ')
                ]
                  .filter(Boolean)
                  .join(' ')
              : ''}
          </Typography>
          <Typography onClick={onClick} sx={{ cursor: 'pointer' }}>
            {accident.contractNumber}{' '}
          </Typography>
        </Stack>
        {accident?.insuranceType && (
          <TransparentChip
            size="small"
            label={accident?.insuranceType.toUpperCase()}
            color="info"
            sx={{ fontSize: '10px', height: '18px' }}
          />
        )}
      </Stack>
      <Typography fontSize="13px">
        {accident.car &&
          [
            [accident.car?.brand, accident.car?.model].filter(Boolean).join(' '),
            [accident.car?.plateNumber].filter(Boolean).join(' | '),
            [accident.contactPerson?.name, accident.contactPerson?.surname].filter(Boolean).join(' ')
          ].join(' | ')}
      </Typography>
    </Grid>
    <Grid size={{ xs: 12, lg: 3 }}>
      <Stack gap={1}>
        {accident?.carRental && !isEmpty(accident?.carRental) ? (
          accident?.carRental?.map((item) =>
            item.isReturned ? (
              <Typography color="success" fontSize="12px">
                Dokončená zápůjčka
              </Typography>
            ) : (
              <RentalInfoBox
                key={item.id}
                type={
                  item.isBlocked && isEmpty(item.car)
                    ? 'warning'
                    : item.isBlocked && !isEmpty(item.car)
                      ? 'red'
                      : 'success'
                }
                reservationDate={item.reservationDate}
                returnDate={item.returnDate}
                rentalDate={item.rentalDate}
                requestDate={item.createdDate}
                carInfo={item.car}
                onClick={() => onRentalClick?.(item.id)}
              />
            )
          )
        ) : (
          <Typography color="info" fontSize="12px">
            Bez zapůjčky a bez žádosti
          </Typography>
        )}
      </Stack>
    </Grid>
    <Grid size={{ xs: 12, lg: 3, xl: 2 }} textAlign="end">
      <Stack direction="row" gap={1} justifyContent="end">
        <IconButton onClick={onClick} color="info">
          <Eye />
        </IconButton>
        <IconButton color="error" onClick={onRemove}>
          <Trash01 />
        </IconButton>
      </Stack>
    </Grid>
  </Grid>
);
